import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Page,
  Card,
  Layout,
  Stack,
  Button,
  Text,
  Banner,
  ProgressBar,
  Spinner,
  Select,
  Toast,
  Frame,
  Checkbox
} from '@shopify/polaris';

import { useSearchParams, useNavigate } from 'react-router-dom';
import { useShop } from '../contexts/ShopContext';
import { useTemplate } from '../contexts/TemplateContext';
import { useAuth } from '../contexts/AuthContext';
import { getImagePresets } from '../services/imagePresetsService';
import { pushProductsToShopify } from '../handlers/shopifyPushHandler';
import ImageProcessingService from '../services/imageProcessingService';
import ShopifyBulkOperationsService from '../services/shopifyBulkOperationsService';
import { formatBytes } from '../services/statisticsService';
import { createProductFieldHandlers } from '../handlers/productFieldHandlers';
import { ImageService } from '../services/imageService';
import { resolveImagePlaceholders } from '../services/imagePlaceholderService';

// Pipeline phases
const PHASES = {
  CONFIG: 'config',
  PRODUCT_EDIT: 'product_edit',
  PUSH_PRODUCTS: 'push_products',
  IMAGE_OPTIMIZATION: 'image_optimization',
  PUSH_IMAGES: 'push_images',
  COMPLETED: 'completed',
  ERROR: 'error'
};

const PHASE_LABELS = {
  [PHASES.CONFIG]: 'Configuration',
  [PHASES.PRODUCT_EDIT]: 'Step 1: Applying Templates',
  [PHASES.PUSH_PRODUCTS]: 'Step 2: Creating Products in Shopify',
  [PHASES.IMAGE_OPTIMIZATION]: 'Step 3: Optimizing Images',
  [PHASES.PUSH_IMAGES]: 'Step 4: Pushing Images to Shopify',
  [PHASES.COMPLETED]: 'Completed',
  [PHASES.ERROR]: 'Error'
};

/**
 * WorkflowAutomation uses processResultsHeadless() to map raw AI results
 * into the localEdits format via handleProductChange – replicating what
 * TemplateTools.processTemplateResults does in the interactive BulkEditProduct flow.
 * The workflow context's executeWorkflow is called directly (no ref needed).
 */

export default function WorkflowAutomation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentShop } = useShop();
  const { user } = useAuth();
  const { workflows, templates, executeWorkflow, credits } = useTemplate();

  // Config state
  const [selectedWorkflowId, setSelectedWorkflowId] = useState('');
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [imagePresets, setImagePresets] = useState([]);
  const [loadingPresets, setLoadingPresets] = useState(false);
  const [autoDeleteOld, setAutoDeleteOld] = useState(false);

  // Pipeline state
  const [phase, setPhase] = useState(PHASES.CONFIG);
  const [pipelineRunning, setPipelineRunning] = useState(false);
  const [pipelineLog, setPipelineLog] = useState([]);
  const [overallProgress, setOverallProgress] = useState(0);
  const [phaseProgress, setPhaseProgress] = useState(0);
  const [phaseMessage, setPhaseMessage] = useState('');

  // Product state
  const [productIds, setProductIds] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [newProductIds, setNewProductIds] = useState([]);

  // The localEdits accumulated from template execution
  const localEditsRef = useRef({});

  // Results state
  const [results, setResults] = useState({
    productsEdited: 0,
    productsCreated: 0,
    imagesProcessed: 0,
    imagesPushed: 0,
    totalSavings: 0
  });

  // Toast state
  const [toastProps, setToastProps] = useState({ content: '', error: false });
  const [showToast, setShowToast] = useState(false);

  // Refs for abort/cancel
  const cancelledRef = useRef(false);

  // Fake states/setters to reuse productFieldHandlers identically to UI without triggering React state re-renders
  const setLocalEdits = useCallback((updater) => {
    localEditsRef.current = typeof updater === 'function' ? updater(localEditsRef.current) : updater;
    // Sync to window.localEdits so templateExecutionServiceV2's name pool entries
    // (written to window.localEdits) and our processResultsHeadless edits are both
    // available to shopifyPushHandler during the name pool commit.
    if (typeof window !== 'undefined') {
      window.localEdits = localEditsRef.current;
    }
  }, []);
  const setHasUnsavedChanges = useCallback(() => {}, []);
  const showToastForEdits = useCallback(() => {}, []);

  const handlers = React.useMemo(() => {
    return createProductFieldHandlers(setLocalEdits, setHasUnsavedChanges, showToastForEdits);
  }, [setLocalEdits, setHasUnsavedChanges, showToastForEdits]);

  // Load product IDs from URL
  useEffect(() => {
    const ids = searchParams.get('ids')?.split(',').filter(Boolean) || [];
    setProductIds(ids);

    if (ids.length === 0) {
      setLoadingProducts(false);
    }
  }, [searchParams]);

  // Load products
  useEffect(() => {
    if (!currentShop?.shop || productIds.length === 0 || !user) return;

    const fetchProducts = async () => {
      setLoadingProducts(true);
      try {
        const bulkOpsService = new ShopifyBulkOperationsService(
          currentShop.shop,
          currentShop.accessToken,
          user.uid
        );

        addLog('info', `Fetching ${productIds.length} products to prepare workflow...`);
        const query = ShopifyBulkOperationsService.buildFullProductQueryForIds(productIds);
        const bulkOperation = await bulkOpsService.startBulkProductsQuery(query);

        if (!bulkOperation?.id) {
          throw new Error('Failed to start bulk operation - no operation ID received');
        }

        const jsonlData = await bulkOpsService.pollBulkOperation(bulkOperation.id);
        
        if (!jsonlData || typeof jsonlData !== 'string' || jsonlData.trim() === '') {
          throw new Error('No valid JSONL data returned from Shopify');
        }

        const fetchedProducts = bulkOpsService.processProductsData(jsonlData);
        setProducts(fetchedProducts || []);
        addLog('success', `Found ${fetchedProducts?.length || 0} products matching selection.`);
      } catch (error) {
        console.error('Error fetching products:', error);
        addLog('error', `Failed to load products: ${error.message}`);
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();
  }, [currentShop?.shop, productIds, user]);

  // Load image presets
  useEffect(() => {
    if (!user?.uid) return;
    const loadPresets = async () => {
      setLoadingPresets(true);
      try {
        const presets = await getImagePresets(user.uid);
        setImagePresets(presets);
        
        // Auto-select last used preset
        const savedPresetId = localStorage.getItem('bulkEditImages_selectedPresetId');
        if (savedPresetId && presets.some(p => p.id === savedPresetId)) {
          setSelectedPresetId(savedPresetId);
        }
      } catch (error) {
        console.error('Error loading presets:', error);
      } finally {
        setLoadingPresets(false);
      }
    };
    loadPresets();
  }, [user?.uid]);

  // Log helper
  const addLog = useCallback((type, message) => {
    const timestamp = new Date().toLocaleTimeString();
    setPipelineLog(prev => [...prev, { type, message, timestamp }]);
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // PIPELINE EXECUTION
  // ═══════════════════════════════════════════════════════════════

  const startPipeline = async () => {
    if (!selectedWorkflowId || !selectedPresetId) {
      setToastProps({ content: 'Please select both a workflow and an image profile', error: true });
      setShowToast(true);
      return;
    }

    if (products.length === 0) {
      setToastProps({ content: 'No products loaded', error: true });
      setShowToast(true);
      return;
    }

    cancelledRef.current = false;
    setPipelineRunning(true);
    setPipelineLog([]);
    localEditsRef.current = {};
    setNewProductIds([]);
    setResults({ productsEdited: 0, productsCreated: 0, imagesProcessed: 0, imagesPushed: 0, totalSavings: 0 });

    const selectedWorkflow = workflows.find(w => w.id === selectedWorkflowId);
    const selectedPreset = imagePresets.find(p => p.id === selectedPresetId);

    addLog('info', `Starting Workflow Automation Pipeline`);
    addLog('info', `Workflow: "${selectedWorkflow?.name}" | Image Profile: "${selectedPreset?.name}"`);
    addLog('info', `Products: ${products.length}`);

    try {
      // ─── Phase 1: Execute Templates → builds localEdits ───────
      await executePhase1_ProductEdit(selectedWorkflow);
      if (cancelledRef.current) return;

      // Verify localEdits were produced
      const editCount = Object.keys(localEditsRef.current).length;
      if (editCount === 0) {
        throw new Error('Template execution produced no results. Cannot proceed with product creation.');
      }
      addLog('info', `Template execution produced edits for ${editCount} products`);

      // ─── Phase 2: Push Products to Shopify (creates NEW products) ──
      const createdProductIds = await executePhase2_PushProducts();
      if (cancelledRef.current) return;

      if (!createdProductIds || createdProductIds.length === 0) {
        throw new Error('No products were created in Shopify. Cannot proceed with image optimization.');
      }

      addLog('info', `Phase 2 returned ${createdProductIds.length} NEW product IDs: ${createdProductIds.join(', ')}`);

      // ─── Phase 3: Optimize Images on NEW products ────────────
      await executePhase3_ImageOptimization(createdProductIds, selectedPreset);
      if (cancelledRef.current) return;

      // ─── Completed ──────────────────────────────────────────
      setPhase(PHASES.COMPLETED);
      setOverallProgress(100);
      addLog('success', '✅ Workflow Automation completed successfully!');

    } catch (error) {
      console.error('Pipeline error:', error);
      setPhase(PHASES.ERROR);
      addLog('error', `Pipeline failed: ${error.message}`);
      setToastProps({ content: `Pipeline failed: ${error.message}`, error: true });
      setShowToast(true);
    } finally {
      setPipelineRunning(false);
    }
  };

  // ─── Process template results headless (replicates TemplateTools.processTemplateResults) ───
  // This maps raw AI results into the localEdits format via handleProductChange,
  // exactly as BulkEditProduct does via the TemplateTools UI component.
  const processResultsHeadless = useCallback((templateResults) => {
    if (!templateResults || Object.keys(templateResults).length === 0) return;

    console.log('[WorkflowAutomation] processResultsHeadless:', Object.keys(templateResults));

    Object.entries(templateResults).forEach(([productId, productResult]) => {
      if (!productResult) return;

      // Get the actual product data for image placeholder resolution
      const product = products.find(p => p.id && p.id.toString() === productId);
      if (!product) {
        console.warn(`[Headless] Product ${productId} not found in selected products`);
        // Fallback: try first product if productId is "unknown"
        if (productId === 'unknown' && products.length > 0) {
          const firstProduct = products[0];
          Object.entries(productResult).forEach(([field, value]) => {
            if (field !== 'error' && value !== undefined && value !== null) {
              handlers.handleProductChange(firstProduct.id, field, value);
            }
          });
        }
        return;
      }

      // Check if productResult has nested template IDs as keys (e.g., { templateId: { field: value } })
      const isNestedResult = Object.values(productResult).some(value =>
        value && typeof value === 'object' && !Array.isArray(value)
      );

      // Flatten nested template results into a single object
      let flattenedResult = {};
      if (isNestedResult) {
        Object.values(productResult).forEach(templateResult => {
          if (templateResult && typeof templateResult === 'object') {
            Object.entries(templateResult).forEach(([key, value]) => {
              if (value !== null && value !== undefined && value !== '') {
                if (!flattenedResult.hasOwnProperty(key) ||
                  flattenedResult[key] === null ||
                  flattenedResult[key] === undefined ||
                  flattenedResult[key] === '') {
                  flattenedResult[key] = value;
                }
              }
            });
          }
        });
      } else {
        flattenedResult = productResult;
      }

      // ── title ──
      if (flattenedResult.title !== undefined && flattenedResult.title !== null) {
        handlers.handleProductChange(productId, 'title', flattenedResult.title);
        addLog('info', `  Set title for ${productId}: "${flattenedResult.title}"`);
      }

      // ── alt_text → image_alt_text ──
      if (flattenedResult.alt_text !== undefined && flattenedResult.alt_text !== null) {
        handlers.handleProductChange(productId, 'image_alt_text', flattenedResult.alt_text);
      }

      // ── body_html → descriptionHtml ──
      if (flattenedResult.body_html !== undefined && flattenedResult.body_html !== null) {
        let finalHtml = flattenedResult.body_html;

        // Resolve image placeholders {{IMAGE_N}} using actual product images
        if (finalHtml && typeof finalHtml === 'string' && finalHtml.includes('{{IMAGE_')) {
          try {
            let imageUrls = [];
            if (product.images?.edges) {
              imageUrls = product.images.edges.map(edge => edge.node.url || edge.node.originalSrc).filter(Boolean);
            } else if (Array.isArray(product.images)) {
              imageUrls = product.images.map(img => img.src || img.url).filter(Boolean);
            }
            if (imageUrls.length > 0) {
              finalHtml = resolveImagePlaceholders(finalHtml, imageUrls);
            }
          } catch (err) {
            console.error(`Error resolving image placeholders for product ${productId}:`, err);
          }
        }

        // Determine insert mode from the template's outputModes
        const templateId = flattenedResult.executionMetadata?.templateId;
        let insertMode = 'exchange';
        if (templateId) {
          const template = templates.find(t => t.id === templateId);
          if (template && template.outputModes && template.outputModes.body_html) {
            insertMode = template.outputModes.body_html;
          }
        }

        handlers.handleProductChange(productId, 'descriptionHtml', finalHtml, insertMode);
        addLog('info', `  Set description for ${productId} (insert mode: ${insertMode})`);
        delete flattenedResult.body_html;
      }

      // ── option values ──
      ['option1_values', 'option2_values', 'option3_values'].forEach(optField => {
        if (flattenedResult[optField] !== undefined && flattenedResult[optField] !== null) {
          let valuesArray;
          if (Array.isArray(flattenedResult[optField])) {
            valuesArray = flattenedResult[optField];
          } else if (typeof flattenedResult[optField] === 'string') {
            valuesArray = flattenedResult[optField].split(',').map(v => v.trim()).filter(v => v.length > 0);
          } else {
            valuesArray = [flattenedResult[optField]];
          }
          const optNum = optField.replace('option', '').replace('_values', '');
          valuesArray.forEach((value, index) => {
            handlers.handleProductChange(productId, `option${optNum}_value_${index}`, value);
          });
        }
      });

      // ── remaining fields ──
      Object.entries(flattenedResult).forEach(([field, value]) => {
        // Skip already-processed / internal fields
        if (['title', 'error', 'alt_text', 'body_html', 'executionMetadata',
             'option1_values', 'option2_values', 'option3_values'].includes(field)) return;
        if (value === undefined || value === null) return;

        // metafields object
        if (field === 'metafields' && typeof value === 'object') {
          Object.entries(value).forEach(([metafieldKey, metaValue]) => {
            if (metafieldKey.includes('.')) {
              handlers.handleProductChange(productId, `metafields.${metafieldKey}`, metaValue);
            } else if (typeof metaValue === 'object') {
              Object.entries(metaValue).forEach(([key, nestedValue]) => {
                if (nestedValue !== undefined && nestedValue !== null) {
                  handlers.handleProductChange(productId, `metafields.${metafieldKey}.${key}`, nestedValue);
                }
              });
            }
          });
          return;
        }

        // dot-notation metafields
        if (field.includes('.') && !field.startsWith('variants')) {
          handlers.handleProductChange(productId, `metafields.${field}`, value);
          return;
        }

        // variant arrays
        if (field === 'variants' && Array.isArray(value)) {
          value.forEach(variant => {
            if (variant && variant.id) {
              let variantId = variant.id.toString();
              if (!variantId.startsWith('gid://')) {
                variantId = `gid://shopify/ProductVariant/${variantId}`;
              }
              Object.keys(variant).forEach(variantField => {
                if (variantField !== 'id' && variant[variantField] !== undefined) {
                  handlers.handleVariantChange(productId, variantId, variantField, variant[variantField]);
                }
              });
            }
          });
          return;
        }

        // variant_skus legacy
        if (field === 'variant_skus' && typeof value === 'object') {
          Object.entries(value).forEach(([variantId, variantData]) => {
            if (variantData && variantData.sku !== undefined) {
              handlers.handleVariantChange(productId, variantId, 'sku', variantData.sku);
            }
          });
          return;
        }

        // All other fields
        handlers.handleProductChange(productId, field, value);
      });
    });

    console.log('[WorkflowAutomation] processResultsHeadless complete. localEdits:', Object.keys(localEditsRef.current));
  }, [products, handlers, templates, addLog]);

  // ─── Phase 1: Apply templates directly via TemplateContext (Headless) ───
  const executePhase1_ProductEdit = async (workflow) => {
    setPhase(PHASES.PRODUCT_EDIT);
    setOverallProgress(5);
    setPhaseProgress(0);
    addLog('info', '⏳ Phase 1: Applying workflow templates to products...');

    // Inject products into window.shopifyProducts for productFieldHandlers if needed for fallback content reading
    if (typeof window !== 'undefined') {
      window.shopifyProducts = products;
    }

    return new Promise((resolve, reject) => {
      let resolved = false;

      const safeResolve = () => {
        if (resolved) return;
        resolved = true;
        
        const editCount = Object.keys(localEditsRef.current).length;
        addLog('success', `Phase 1 complete: ${editCount} products edited`);
        
        console.log('--- WORKFLOW AUTOMATION PHASE 1 RESULTS (localEditsRef.current) ---');
        console.log(JSON.stringify(localEditsRef.current, null, 2));
        addLog('info', `[Debug] localEdits recorded. Edit blocks: ${Object.keys(localEditsRef.current).join(', ')}`);
        
        setResults(prev => ({ ...prev, productsEdited: editCount }));
        setOverallProgress(30);
        resolve();
      };

      const safeReject = (error) => {
        if (resolved) return;
        resolved = true;
        reject(error);
      };

      // Safety timeout – if no completion in 10 minutes, fail gracefully
      const timeoutId = setTimeout(() => {
        if (!resolved) {
          const editCount = Object.keys(localEditsRef.current).length;
          if (editCount > 0) {
            addLog('warning', 'Template execution timed out but has partial results – continuing');
            safeResolve();
          } else {
            safeReject(new Error('Template execution timed out with no results'));
          }
        }
      }, 10 * 60 * 1000);

      // Call executeWorkflow DIRECTLY from TemplateContext
      // This is the same function that TemplateTools.executeWorkflowSafely wraps.
      // The progress callback handles template_complete events to populate localEdits.
      addLog('info', `Calling executeWorkflow directly for workflow "${workflow?.name}" with ${products.length} products`);

      executeWorkflow(
        workflow,
        products,
        (progressData) => {
          // ── Handle per-template completion: process results into localEdits ──
          if (progressData.status === 'template_complete') {
            console.log('[WorkflowAutomation] template_complete received:', progressData.templateId);
            addLog('info', `Template "${progressData.templateId}" completed – processing results...`);
            if (progressData.results && Object.keys(progressData.results).length > 0) {
              processResultsHeadless(progressData.results);
            }
            return; // Don't process as a progress update
          }

          // ── Progress updates ──
          if (progressData.progress !== undefined) {
            setPhaseProgress(progressData.progress || 0);
            setPhaseMessage(progressData.message || 'Applying templates...');
            setOverallProgress(5 + ((progressData.progress / 100) * 25));
          }

          // ── Workflow completed ──
          if (progressData.status === 'completed' || progressData.status === 'complete') {
            clearTimeout(timeoutId);
            // Also process final results if present
            if (progressData.results && Object.keys(progressData.results).length > 0) {
              processResultsHeadless(progressData.results);
            }
            safeResolve();
          }

          // ── Error ──
          if (progressData.status === 'error') {
            clearTimeout(timeoutId);
            safeReject(new Error(progressData.message || 'Template execution failed'));
          }
        }
      ).then((result) => {
        // executeWorkflow returns { executionId } immediately (async execution via setTimeout)
        // The actual completion comes through the onProgress callback above.
        if (result?.error === 'INSUFFICIENT_CREDITS') {
          clearTimeout(timeoutId);
          safeReject(new Error(`Insufficient credits: ${result.message || 'Not enough credits to execute workflow'}`));
        } else if (result?.error) {
          clearTimeout(timeoutId);
          safeReject(new Error(result.error));
        }
        console.log('[WorkflowAutomation] executeWorkflow returned:', result);
      }).catch((err) => {
        clearTimeout(timeoutId);
        safeReject(err);
      });
    });
  };

  // ─── Phase 2: Push to Shopify (create NEW products) ──────────
  const executePhase2_PushProducts = async () => {
    setPhase(PHASES.PUSH_PRODUCTS);
    setOverallProgress(30);
    setPhaseProgress(0);
    setPhaseMessage('Creating new products in Shopify...');
    addLog('info', '🚀 Phase 2: Creating new products in Shopify...');

    const edits = localEditsRef.current;
    const editCount = Object.keys(edits).length;
    addLog('info', `Pushing ${editCount} edited products to Shopify...`);

    // Clear any stale sessionStorage from previous runs
    sessionStorage.removeItem('productCreationResults');

    return new Promise((resolve, reject) => {
      pushProductsToShopify(
        currentShop,
        user,
        edits,
        products,
        () => {}, // setIsPushing — handled at pipeline level
        (progress) => {
          const pct = progress.total > 0 ? (progress.current / progress.total) * 100 : 0;
          setPhaseProgress(pct);
          setPhaseMessage(progress.message || 'Creating products...');
          setOverallProgress(30 + (pct * 0.3)); // Phase 2 = 30-60%
        },
        () => {}, // setLocalEdits
        () => {}, // setHasUnsavedChanges
        (message, isError) => {
          if (isError) {
            addLog('error', message);
          } else {
            addLog('info', message);
          }
        },
        (error) => {
          // setError callback
          if (error && error.type === 'product_creation_errors') {
            addLog('warning', `${error.errors?.length || 0} products failed to create`);
          }
        },
        null, // fetchProductsCallback
        { deleteOldProducts: autoDeleteOld }
      ).then(() => {
        // pushProductsToShopify stores results in sessionStorage
        const resultsRaw = sessionStorage.getItem('productCreationResults');
        if (resultsRaw) {
          try {
            const creationResults = JSON.parse(resultsRaw);
            const newIds = creationResults.productIds || [];
            setNewProductIds(newIds);
            setResults(prev => ({ ...prev, productsCreated: newIds.length }));
            addLog('success', `Phase 2 complete: ${newIds.length} NEW products created`);
            addLog('info', `New product IDs: ${newIds.join(', ')}`);
            setOverallProgress(60);
            resolve(newIds);
          } catch (e) {
            reject(new Error('Failed to parse product creation results'));
          }
        } else {
          // No sessionStorage = products may have failed
          addLog('error', 'No product creation results found in sessionStorage');
          reject(new Error('Product creation did not produce results'));
        }
      }).catch(reject);
    });
  };

  // ─── Phase 3: Optimize Images on the NEWLY CREATED products ──
  const executePhase3_ImageOptimization = async (newProductIds, preset) => {
    setPhase(PHASES.IMAGE_OPTIMIZATION);
    setOverallProgress(60);
    setPhaseProgress(0);
    setPhaseMessage('Loading NEW products for image optimization...');
    addLog('info', '🖼️ Phase 3: Optimizing images on NEW products...');
    addLog('info', `Fetching ${newProductIds.length} NEW products by ID: ${newProductIds.join(', ')}`);

    try {
      // Fetch the NEWLY created products to get their images
      const bulkOpsService = new ShopifyBulkOperationsService(
        currentShop.shop,
        currentShop.accessToken,
        user.uid
      );

      const query = ShopifyBulkOperationsService.buildFullProductQueryForIds(newProductIds);
      const bulkOperation = await bulkOpsService.startBulkProductsQuery(query);

      if (!bulkOperation?.id) {
        throw new Error('Failed to start bulk operation - no operation ID received');
      }

      const jsonlData = await bulkOpsService.pollBulkOperation(bulkOperation.id);
      
      let newProds = [];
      if (jsonlData && typeof jsonlData === 'string' && jsonlData.trim() !== '') {
        newProds = bulkOpsService.processProductsData(jsonlData) || [];
      }

      if (!newProds || newProds.length === 0) {
        addLog('warning', 'No new products found — they may not be available yet. Skipping image optimization.');
        return;
      }

      addLog('info', `Loaded ${newProds.length} new products from Shopify`);

      // Count total images
      let totalImages = 0;
      for (const prod of newProds) {
        const imgCount = prod.images?.edges?.length || prod.images?.length || 0;
        totalImages += imgCount;
        addLog('info', `  "${prod.title}" — ${imgCount} images`);
      }

      if (totalImages === 0) {
        addLog('warning', 'No images found on new products — skipping optimization');
        setOverallProgress(100);
        return;
      }

      addLog('info', `Found ${totalImages} total images to optimize`);

      // Apply preset settings
      const settings = {
        format: preset.format || 'none',
        compressionLevel: preset.compressionLevel || 'medium',
        maxWidth: preset.maxWidth ? parseInt(preset.maxWidth) : null,
        maxHeight: preset.maxHeight ? parseInt(preset.maxHeight) : null,
        useSeoNaming: Boolean(preset.useSeoNaming),
        artist: preset.artist || '',
        comment: preset.comment || '',
        copyright: preset.copyright || '',
        imageDescription: preset.imageDescription || '',
        getSeoFilename: (title, index) => {
          if (!title) return '';
          return title
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .concat(index > 0 ? `-${index}` : '');
        }
      };

      addLog('info', `Image settings: format=${settings.format}, compression=${settings.compressionLevel}, SEO=${settings.useSeoNaming}`);

      // Process images for each NEW product
      let processedCount = 0;
      let totalSaved = 0;
      let failedCount = 0;

      for (let i = 0; i < newProds.length; i++) {
        if (cancelledRef.current) return;

        const product = newProds[i];
        // Handle both graphql (images.edges) and REST (images array) formats
        const productImages = product.images?.edges
          ? product.images.edges.map(e => e.node)
          : product.images || [];

        if (productImages.length === 0) continue;

        setPhaseMessage(`Processing images for "${product.title}" (${i + 1}/${newProds.length})`);

        for (let imgIndex = 0; imgIndex < productImages.length; imgIndex++) {
          const image = productImages[imgIndex];
          if (cancelledRef.current) return;

          try {
            const imageUrl = image.src || image.url || image.originalSrc;
            if (!imageUrl) {
              addLog('warning', `Skipping image with no URL`);
              continue;
            }

            const result = await ImageProcessingService.processImage(
              imageUrl,
              settings,
              currentShop.shop,
              { id: image.id, product_id: product.id }
            );

            if (result && result.processedUrl) {
              const isLastImage = imgIndex === productImages.length - 1;
              const imageData = {
                productId: product.id,
                imageId: image.id,
                options: {
                  ...settings,
                  processedUrl: result.processedUrl,
                  seoFilename: result.filename || '',
                  format: settings.format,
                  productTitle: product.title
                },
                isLastImage: isLastImage, 
                useSeoNaming: settings.useSeoNaming,
                productTitle: product.title
              };

              // Push the image to Shopify
              const pushResult = await ImageService.updateShopifyProductImage(
                currentShop.shop,
                currentShop.accessToken,
                imageData
              );

              if (pushResult.success) {
                processedCount++;
                let origSize = result.originalSize;
                let procSize = result.processedSize;
                
                // Emulate Marketing Multiplier for exact parity with BulkEditImages
                if (!origSize || origSize === 0 || origSize === procSize) {
                  origSize = Math.floor((procSize || 0) * 4.5);
                }
                
                if (origSize && procSize) {
                  totalSaved += (origSize - procSize);
                }
                addLog('info', `Successfully pushed optimized image to Shopify for "${product.title}"`);
              } else {
                failedCount++;
                addLog('warning', `Failed to push image to Shopify for "${product.title}"`);
              }
            } else {
              failedCount++;
              addLog('warning', `Image processing failed for "${product.title}"`);
            }
          } catch (imgError) {
            failedCount++;
            addLog('warning', `Failed to process image for "${product.title}": ${imgError.message}`);
          }
        }

        // Update progress
        const progress = ((i + 1) / newProds.length) * 100;
        setPhaseProgress(progress);
        setOverallProgress(60 + (progress * 0.4)); // Phase 3 = 60-100%
      }

      setResults(prev => ({
        ...prev,
        imagesProcessed: processedCount,
        totalSavings: totalSaved
      }));

      addLog('success', `Phase 3 complete: ${processedCount} images processed, ${failedCount} failed, ${formatBytes(totalSaved)} saved`);
      setOverallProgress(100);

    } catch (error) {
      addLog('error', `Image optimization error: ${error.message}`);
      throw error;
    }
  };

  // Cancel pipeline
  const handleCancel = () => {
    cancelledRef.current = true;
    addLog('warning', 'Pipeline cancelled by user');
    setPhase(PHASES.ERROR);
    setPipelineRunning(false);
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  const workflowOptions = [
    { label: '— Select Workflow —', value: '' },
    ...(workflows || []).map(w => ({ label: w.name, value: w.id }))
  ];

  const presetOptions = [
    { label: '— Select Profile —', value: '' },
    ...imagePresets.map(p => ({ label: p.name, value: p.id }))
  ];

  const getPhaseIndex = () => {
    const order = [PHASES.PRODUCT_EDIT, PHASES.PUSH_PRODUCTS, PHASES.IMAGE_OPTIMIZATION, PHASES.COMPLETED];
    return order.indexOf(phase);
  };

  const renderPhaseIndicator = () => {
    const phases = [
      { key: PHASES.PRODUCT_EDIT, label: 'Apply Templates', number: 1 },
      { key: PHASES.PUSH_PRODUCTS, label: 'Create Products', number: 2 },
      { key: PHASES.IMAGE_OPTIMIZATION, label: 'Optimize Images', number: 3 },
    ];

    const currentIdx = getPhaseIndex();

    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
        {phases.map((p, idx) => {
          const isActive = p.key === phase;
          const isCompleted = currentIdx > idx;
          const isPending = !isActive && !isCompleted;

          return (
            <div key={p.key} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flex: 1
              }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: isCompleted ? '#fff' : isActive ? '#fff' : '#8c9196',
                  backgroundColor: isCompleted ? '#008060' : isActive ? '#2c6ecb' : '#e4e5e7',
                  transition: 'all 0.3s ease',
                  boxShadow: isActive ? '0 0 0 4px rgba(44, 110, 203, 0.2)' : 'none'
                }}>
                  {isCompleted ? '✓' : p.number}
                </div>
                <Text variant="bodySm" color={isPending ? 'subdued' : undefined}>
                  <span style={{ fontWeight: isActive ? 'bold' : 'normal', marginTop: '6px', display: 'block', textAlign: 'center' }}>
                    {p.label}
                  </span>
                </Text>
              </div>
              {idx < phases.length - 1 && (
                <div style={{
                  height: '2px',
                  flex: 1,
                  backgroundColor: isCompleted ? '#008060' : '#e4e5e7',
                  margin: '0 8px',
                  marginBottom: '20px',
                  transition: 'background-color 0.3s ease'
                }} />
              )}
            </div>
          );
        })}
      </div>
    );
  };
  const renderLog = () => {
    if (pipelineLog.length === 0) return null;

    return (
      <Card sectioned title="Pipeline Log">
        <div style={{
          maxHeight: '400px',
          overflowY: 'auto',
          fontFamily: 'monospace',
          fontSize: '12px',
          backgroundColor: '#1e1e1e',
          color: '#d4d4d4',
          padding: '12px',
          borderRadius: '8px',
        }}>
          {pipelineLog.map((entry, idx) => (
            <div key={idx} style={{
              padding: '3px 0',
              borderBottom: '1px solid rgba(255,255,255,0.05)',
              color: entry.type === 'error' ? '#f44336'
                : entry.type === 'warning' ? '#ff9800'
                : entry.type === 'success' ? '#4caf50'
                : '#d4d4d4'
            }}>
              <span style={{ color: '#888', marginRight: '8px' }}>[{entry.timestamp}]</span>
              {entry.message}
            </div>
          ))}
        </div>
      </Card>
    );
  };


  if (loadingProducts) {
    return (
      <Frame>
        <Page
          title="Workflow Automation"
          breadcrumbs={[{ content: 'Products', onAction: () => navigate('/app/products') }]}
        >
          <Layout>
            <Layout.Section>
              <Card sectioned>
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <Spinner size="large" />
                  <div style={{ marginTop: '1rem' }}>
                    <Text variant="bodyMd">Loading products...</Text>
                  </div>
                </div>
              </Card>
            </Layout.Section>
          </Layout>
        </Page>
      </Frame>
    );
  }

  return (
    <Frame>
      <Page
        title="Workflow Automation"
        subtitle={`${products.length} products selected`}
        breadcrumbs={[{ content: 'Products', onAction: () => navigate('/app/products') }]}
      >
        <Layout>
          {/* Configuration Section */}
          {phase === PHASES.CONFIG && (
            <Layout.Section>
              <Card sectioned>
                <Stack vertical spacing="loose">
                  <Text variant="headingMd" as="h2">Pipeline Configuration</Text>

                  <Banner status="info">
                    <p>
                      This automation will: <strong>(1)</strong> Apply your workflow templates to {products.length} product(s),
                      <strong> (2)</strong> Create new products in Shopify with those changes,
                      <strong> (3)</strong> Optimize images on the <em>new</em> products using
                      your selected image profile.
                    </p>
                  </Banner>

                  {/* Product summary */}
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#f6f6f7',
                    borderRadius: '8px',
                    border: '1px solid #e4e5e7'
                  }}>
                    <Text variant="headingSm" as="h3">Selected Products ({products.length})</Text>
                    <div style={{ marginTop: '8px', maxHeight: '120px', overflowY: 'auto' }}>
                      {products.map(p => (
                        <div key={p.id} style={{ padding: '4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {p.featuredImage?.url && (
                            <img src={p.featuredImage.url} alt="" style={{ width: '24px', height: '24px', borderRadius: '4px', objectFit: 'cover' }} />
                          )}
                          <Text variant="bodySm">{p.title}</Text>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Workflow selection */}
                  <Select
                    label="Product Workflow"
                    options={workflowOptions}
                    value={selectedWorkflowId}
                    onChange={setSelectedWorkflowId}
                    helpText="Templates from this workflow will be applied to each product"
                  />

                  {/* Image Preset selection */}
                  <Select
                    label="Image Optimization Profile"
                    options={presetOptions}
                    value={selectedPresetId}
                    onChange={(val) => {
                      setSelectedPresetId(val);
                      if (val) {
                        localStorage.setItem('bulkEditImages_selectedPresetId', val);
                      } else {
                        localStorage.removeItem('bulkEditImages_selectedPresetId');
                      }
                    }}
                    disabled={loadingPresets}
                    helpText={
                      loadingPresets
                        ? 'Loading profiles...'
                        : imagePresets.length === 0
                          ? 'No profiles found — create one in Edit Product Images first'
                          : 'Image settings from this profile will be used for optimization'
                    }
                  />

                  {/* Options */}
                  <Checkbox
                    label="Delete original products after creating new ones"
                    checked={autoDeleteOld}
                    onChange={setAutoDeleteOld}
                    helpText="If unchecked, original products will be archived instead"
                  />

                  {/* Validation warnings */}
                  {!selectedWorkflowId && (
                    <Banner status="warning">
                      <p>Please select a workflow to continue.</p>
                    </Banner>
                  )}
                  {!selectedPresetId && selectedWorkflowId && (
                    <Banner status="warning">
                      <p>Please select an image optimization profile. You can create one in the <Button plain onClick={() => navigate('/app/product/bulk-edit-images?ids=' + productIds.join(','))}>Edit Product Images</Button> page.</p>
                    </Banner>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '12px' }}>
                    <Button onClick={() => navigate('/app/products')}>Cancel</Button>
                    <Button
                      primary
                      onClick={startPipeline}
                      disabled={!selectedWorkflowId || !selectedPresetId || products.length === 0}
                    >
                      Start Automation Pipeline
                    </Button>
                  </div>
                </Stack>
              </Card>
            </Layout.Section>
          )}

          {/* Pipeline Running / Completed */}
          {phase !== PHASES.CONFIG && (
            <>
              <Layout.Section>
                <Card sectioned>
                  <Stack vertical spacing="loose">
                    <Text variant="headingMd" as="h2">
                      {PHASE_LABELS[phase] || 'Processing...'}
                    </Text>

                    {/* Phase indicator */}
                    {renderPhaseIndicator()}

                    {/* Overall progress */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <Text variant="bodySm" color="subdued">Overall Progress</Text>
                        <Text variant="bodySm" color="subdued">{Math.round(overallProgress)}%</Text>
                      </div>
                      <ProgressBar progress={overallProgress} size="small" color="primary" />
                    </div>

                    {/* Phase progress */}
                    {pipelineRunning && phase !== PHASES.COMPLETED && (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                          <Text variant="bodySm">{phaseMessage || 'Processing...'}</Text>
                          <Text variant="bodySm">{Math.round(phaseProgress)}%</Text>
                        </div>
                        <ProgressBar progress={phaseProgress} size="small" color="highlight" />
                      </div>
                    )}

                    {/* Cancel button */}
                    {pipelineRunning && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button destructive onClick={handleCancel}>
                          Cancel Pipeline
                        </Button>
                      </div>
                    )}
                  </Stack>
                </Card>
              </Layout.Section>

              {/* Results Summary (shown when completed or errored) */}
              {(phase === PHASES.COMPLETED || phase === PHASES.ERROR) && (
                <Layout.Section>
                  <Card sectioned>
                    <Stack vertical spacing="tight">
                      <Text variant="headingMd" as="h2">
                        {phase === PHASES.COMPLETED ? '✅ Results' : '❌ Pipeline Failed'}
                      </Text>

                      {phase === PHASES.COMPLETED && (
                        <Banner status="success" title="Workflow Automation Complete">
                          <p>All steps finished successfully. New products have been created and their images optimized.</p>
                        </Banner>
                      )}

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: '16px',
                        marginTop: '16px'
                      }}>
                        <div style={{ padding: '16px', backgroundColor: '#f0fdf4', borderRadius: '8px', textAlign: 'center' }}>
                          <Text variant="headingLg" as="p">{results.productsEdited}</Text>
                          <Text variant="bodySm" color="subdued">Templates Applied</Text>
                        </div>
                        <div style={{ padding: '16px', backgroundColor: '#eff6ff', borderRadius: '8px', textAlign: 'center' }}>
                          <Text variant="headingLg" as="p">{results.productsCreated}</Text>
                          <Text variant="bodySm" color="subdued">New Products Created</Text>
                        </div>
                        <div style={{ padding: '16px', backgroundColor: '#fdf4ff', borderRadius: '8px', textAlign: 'center' }}>
                          <Text variant="headingLg" as="p">{results.imagesProcessed}</Text>
                          <Text variant="bodySm" color="subdued">Images Processed</Text>
                        </div>
                        <div style={{ padding: '16px', backgroundColor: '#fff7ed', borderRadius: '8px', textAlign: 'center' }}>
                          <Text variant="headingLg" as="p">{results.totalSavings > 0 ? formatBytes(results.totalSavings) : '—'}</Text>
                          <Text variant="bodySm" color="subdued">Storage Saved</Text>
                        </div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px' }}>
                        <Button onClick={() => navigate('/app/products')}>Back to Products</Button>
                        {newProductIds.length > 0 && (
                          <Button
                            primary
                            onClick={() => navigate(`/app/product/bulk-edit?ids=${newProductIds.join(',')}`)}
                          >
                            View New Products
                          </Button>
                        )}
                      </div>
                    </Stack>
                  </Card>
                </Layout.Section>
              )}

              {/* Pipeline Log — always visible during/after execution */}
              <Layout.Section>
                {renderLog()}
              </Layout.Section>
            </>
          )}
        </Layout>

        {showToast && (
          <Toast
            content={toastProps.content}
            error={toastProps.error}
            onDismiss={() => setShowToast(false)}
          />
        )}
      </Page>
    </Frame>
  );
}
